<script setup lang="ts">
import type { PropType } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { SquareArrowOutUpRightIcon, XIcon } from 'lucide-vue-next'
import { computed } from 'vue'
import { colorMap } from '~/utils/badge'

const props = defineProps({
  color: {
    type: String as PropType<keyof typeof colorMap>,
    validator: (value: string) => {
      return ['primary', 'secondary', 'neutral', 'accent', 'success', 'error', 'info', 'warning'].includes(value)
    },
  },
  to: { type: [String, Object] as PropType<RouteLocationRaw> },
  text: { type: String, default: '' },
  removable: { type: Boolean, default: false },
})

const emit = defineEmits<{
  (e: 'removed', text: string): void
}>()

const router = useRouter()

const backgroundColor = computed(() => {
  return props.color ? colorMap[props.color].bg : ''
})

const textColor = computed(() => {
  return props.color ? colorMap[props.color].text : ''
})

const hoverColor = computed(() => {
  return props.to && props.color ? colorMap[props.color].hover : ''
})

function remove() {
  emit('removed', props.text)
}

function goTo(e: MouseEvent) {
  if (props.to) {
    e.stopPropagation()
    router.push(props.to)
  }
}
</script>

<template>
  <span
    v-if="text || $slots.default"
    class="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full
    text-xs font-medium w-fit h-fit whitespace-nowrap gap-2 group/badge"
    :class="[
      backgroundColor,
      textColor,
      hoverColor,
      $attrs.class,
      { 'cursor-pointer': to || removable },
    ]"
    @click="goTo"
  >
    <slot>
      {{ text }}
    </slot>

    <XIcon
      v-if="removable"
      :size="12"
      class="text-base-content/50"
      @click="remove"
    />

    <SquareArrowOutUpRightIcon
      v-if="to"
      :size="12"
      class="text-base-content/50 group-hover/badge:text-primary-content"
    />
  </span>
</template>
